import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { GlobalDialer } from '@/components/voice/GlobalDialer';
import { useLocation } from 'wouter';

interface DialerOptions {
  phoneNumber?: string;
  contactName?: string;
  contactId?: string;
}

interface GlobalDialerContextType {
  isDialerOpen: boolean;
  dialerOptions: DialerOptions;
  openDialer: (options?: DialerOptions) => void;
  closeDialer: () => void;
  initCall: (phoneNumber: string, callerId?: string) => Promise<void>;
}

const GlobalDialerContext = createContext<GlobalDialerContextType | undefined>(undefined);

export function useGlobalDialer() {
  const context = useContext(GlobalDialerContext);
  if (!context) {
    throw new Error('useGlobalDialer must be used within a GlobalDialerProvider');
  }
  return context;
}

interface GlobalDialerProviderProps {
  children: ReactNode;
}

export function GlobalDialerProvider({ children }: GlobalDialerProviderProps) {
  const [isDialerOpen, setIsDialerOpen] = useState(false);
  const [dialerOptions, setDialerOptions] = useState<DialerOptions>({});
  const [location] = useLocation();
  const isOnboardingPage = location.startsWith('/onboarding');

  const openDialer = useCallback((options: DialerOptions = {}) => {
    setDialerOptions(options);
    setIsDialerOpen(true);
  }, []);

  const closeDialer = useCallback(() => {
    setIsDialerOpen(false);
    setDialerOptions({});
  }, []);

  // Listen for custom openDialer events
  useEffect(() => {
    const handleOpenDialer = (event: CustomEvent) => {
      const { number, contactName } = event.detail;
      openDialer({ phoneNumber: number, contactName });
    };

    window.addEventListener('openDialer', handleOpenDialer as EventListener);
    
    return () => {
      window.removeEventListener('openDialer', handleOpenDialer as EventListener);
    };
  }, [openDialer]);

  const initCall = useCallback(async (phoneNumber: string, callerId?: string) => {
    try {
      console.log('📞 Initiating call:', { phoneNumber, callerId });
      
      // Make the call using the existing voice system
      const response = await fetch('/api/voice/call', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'X-Organization-ID': localStorage.getItem('organizationId') || 'default-org'
        },
        body: JSON.stringify({
          to: phoneNumber,
          from: callerId
        })
      });

      const result = await response.json();
      console.log('📞 Call response:', result);

      if (!response.ok) {
        throw new Error(result.message || 'Failed to initiate call');
      }

      return result;
    } catch (error: any) {
      console.error('❌ Call initiation failed:', error);
      throw new Error(error.message || 'Failed to initiate call');
    }
  }, []);

  const value = {
    isDialerOpen,
    dialerOptions,
    openDialer,
    closeDialer,
    initCall
  };

  return (
    <GlobalDialerContext.Provider value={value}>
      {children}
      {isDialerOpen && !isOnboardingPage && !location.startsWith('/mobile') && (
        <GlobalDialer
          onClose={closeDialer}
          initialNumber={dialerOptions.phoneNumber}
          contactName={dialerOptions.contactName}
        />
      )}
    </GlobalDialerContext.Provider>
  );
}